import React, { useState, useRef, useEffect, useCallback } from "react";

function EditableTab({ rig, isActive, isOverLimit, isDraft, onSwitch, onRename, onDragStart, onDragOver, onDragEnd, onDrop, isDragTarget }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const inputRef = useRef(null);
  const value = draft ?? rig.name;

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleCommit = () => {
    setEditing(false);
    const trimmed = value.trim();
    if (trimmed && trimmed !== rig.name) {
      onRename(rig.id, trimmed);
    }
    setDraft(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCommit();
    } else if (e.key === "Escape") {
      setDraft(null);
      setEditing(false);
    }
  };

  const handleDoubleClick = () => {
    if (isActive && !isOverLimit && !isDraft) {
      setEditing(true);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="board-tab board-tab--editing"
        value={value}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleCommit}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (isOverLimit) {
    return (
      <button
        role="tab"
        aria-selected={false}
        aria-disabled="true"
        className="board-tab board-tab--over-limit"
        type="button"
      >
        {rig.name}
      </button>
    );
  }

  return (
    <button
      role="tab"
      aria-selected={isActive}
      className={`board-tab ${isActive ? "board-tab--active" : ""}${isDraft ? " board-tab--draft" : ""}${isDragTarget ? " board-tab--drop-target" : ""}`}
      onClick={() => onSwitch(rig.id)}
      onDoubleClick={handleDoubleClick}
      type="button"
      draggable={!isDraft}
      onDragStart={isDraft ? undefined : (e) => onDragStart(e, rig.id)}
      onDragOver={isDraft ? undefined : (e) => onDragOver(e, rig.id)}
      onDragEnd={isDraft ? undefined : onDragEnd}
      onDrop={isDraft ? undefined : (e) => onDrop(e, rig.id)}
    >
      {rig.name}
    </button>
  );
}

export default function BoardTabs({ rigs, currentRigId, maxSavedRigs, onSwitch, onNew, onRename, onReorder }) {
  const activeRigCount = rigs.filter((r) => !r.over_limit && !r.draft).length;
  const atLimit = activeRigCount >= maxSavedRigs;
  const hasDraft = rigs.some((r) => r.draft);
  const [dragOverId, setDragOverId] = useState(null);
  const dragSourceId = useRef(null);

  const handleDragStart = useCallback((e, rigId) => {
    dragSourceId.current = rigId;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(rigId));
    e.currentTarget.classList.add("board-tab--dragging");
  }, []);

  const handleDragOver = useCallback((e, rigId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (rigId !== dragSourceId.current) {
      setDragOverId(rigId);
    }
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.currentTarget.classList.remove("board-tab--dragging");
    dragSourceId.current = null;
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback((e, targetId) => {
    e.preventDefault();
    setDragOverId(null);

    const sourceId = dragSourceId.current;
    dragSourceId.current = null;
    if (!sourceId || sourceId === targetId) return;

    const oldIndex = rigs.findIndex((r) => r.id === sourceId);
    const newIndex = rigs.findIndex((r) => r.id === targetId);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...rigs];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    if (onReorder) {
      onReorder(reordered);
    }
  }, [rigs, onReorder]);

  return (
    <div className="board-tabs" role="tablist">
      {rigs.map((rig) => (
        <EditableTab
          key={rig.id}
          rig={rig}
          isActive={rig.id === currentRigId}
          isOverLimit={!!rig.over_limit}
          isDraft={!!rig.draft}
          onSwitch={onSwitch}
          onRename={onRename}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          isDragTarget={dragOverId === rig.id}
        />
      ))}
      {(!atLimit || !hasDraft) && (
        <button
          className="board-tab board-tab--new"
          onClick={onNew}
          type="button"
          role="tab"
          aria-selected={false}
          aria-label="New Board"
        >
          +
        </button>
      )}
    </div>
  );
}
